// src/hooks/useVendorQueries.js
// All React Query hooks for the Vendors page (crane / recoveryVan / craneAndRecoveryVan)
// Mirrors the pattern in useWorkshopQueries.js

import {
  useInfiniteQuery,
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import api from "../axiosInstance";

// ─── Query key factory ────────────────────────────────────────────────────────
export const VENDOR_KEYS = {
  all:      ()         => ["vendors"],
  list:     (filters)  => ["vendors", "list", filters],
  detail:   (code)     => ["vendors", "detail", code],
  cases:    (code)     => ["vendors", "cases", code],
  nearby:   (coords)   => ["vendors", "nearby", coords],
};

// ─── Normaliser — converts one raw DB row to a clean frontend shape ───────────
// Called in both useVendors (list) and useVendorDetail (single).
function normaliseVendor(v) {
  return {
    // identity
    id:                   v.id,
    vendorCode:           v.vendorCode           || null,
    vendorName:           v.vendorName           || null,
    companyName:          v.companyName          || null,
    vendorType:           v.vendorType           || null,   // "crane" | "recoveryVan" | "craneAndRecoveryVan"

    // contact
    vendorPhone:          v.vendorPhone          || null,
    email:                v.email                || null,
    login_mobile:         v.login_mobile         || null,
    contactPerson:        v.contactPerson        || null,
    contactPersonNum:     v.contactPersonNum     || null,
    contactPersonNum2:    v.contactPersonNum2    || null,

    // address
    address:              v.address              || null,
    district:             v.district             || null,
    state:                v.state                || null,
    pincode:              v.pincode              || null,

    // KYC / tax
    GSTNo:                v.GSTNo                || null,
    panNo:                v.panNo                || null,
    adharNo:              v.adharNo              || null,
    fatherName:           v.fatherName           || null,

    // business
    vehicleNo:            v.vehicleNo            || null,
    rate:                 v.rate    != null ? Number(v.rate) : null,
    perHR:                v.perHR                || null,

    // agreement
    typeOfAgreement:      v.typeOfAgreement      || null,
    // backend stores 0/1 int — coerce to boolean
    agreementSigned:      v.agreementSigned === true || v.agreementSigned === 1 || v.agreementSigned === "true",

    // status — backend stores "true"/"false" strings
    isActive:             v.isActive === true || v.isActive === 1 || v.isActive === "true",

    // location
    latitude:             v.latitude  != null ? Number(v.latitude)  : null,
    longitude:            v.longitude != null ? Number(v.longitude) : null,
    location_updated_at:  v.location_updated_at  || null,

    // documents (S3 URLs)
    GST:                  v.GST                  || null,
    adharCard:            v.adharCard            || null,
    panCard:              v.panCard              || null,
    agreement:            v.agreement            || null,
    businessRegistration: v.businessRegistration || null,
    cancelledCheque:      v.cancelledCheque      || null,
    profileImage:         v.profileImage         || null,
    images:               safeJson(v.images),

    // misc
    remark:               v.remark               || null,
    addedTime:            v.addedTime            || null,
    currentStatus:        v.currentStatus        || null,
    ReasonOfRejectionKYC: v.ReasonOfRejectionKYC || null,
  };
}

// ─── useVendors — infinite-scroll list (mirrors useWorkshops) ─────────────────
//
// Response shape from GET /api/admin/vendors:
//   { success, nextCursor, metrics, data: [...] }
//
export function useVendors(filters = {}) {
  return useInfiniteQuery({
    queryKey: VENDOR_KEYS.list(filters),

    queryFn: async ({ pageParam = null, signal }) => {
      const params = new URLSearchParams();

      // Forward every active filter to the backend
      if (filters.search)          params.append("search",          filters.search);
      if (filters.vendorType)      params.append("vendorType",      filters.vendorType);
      if (filters.isActive != null) params.append("isActive",       filters.isActive);
      if (filters.district)        params.append("district",        filters.district);
      if (filters.agreementFilter) params.append("agreementFilter", filters.agreementFilter);
      if (filters.sortBy)          params.append("sortBy",          filters.sortBy);
      if (pageParam)               params.append("cursor",          pageParam);

      const { data } = await api.get(`/api/admin/vendors?${params}`, { signal });

      const raw = data?.data || [];

      return {
        vendors:    raw.map(normaliseVendor),
        metrics:    data?.metrics    || {},
        total:      data?.total      ?? raw.length,
        nextCursor: data?.nextCursor || null,
      };
    },

    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    initialPageParam: null,

    // Only fire search queries when the user has typed ≥ 2 chars
    enabled: filters.search ? filters.search.length >= 2 : true,

    staleTime:            1000 * 60 * 5,   // 5 min
    gcTime:               1000 * 60 * 30,  // 30 min
    refetchOnWindowFocus: false,
    refetchOnReconnect:   false,
    refetchOnMount:       false,
    placeholderData:      (prev) => prev,
  });
}

// ─── useVendorDetail — single vendor by vendorCode ────────────────────────────
//
// Response shape from GET /api/admin/vendors/:vendorCode:
//   { success, data: { ...vendor } }
//
export function useVendorDetail(vendorCode) {
  return useQuery({
    queryKey: VENDOR_KEYS.detail(vendorCode),
    queryFn: async () => {
      const { data } = await api.get(`/api/admin/vendors/${vendorCode}`);
      return normaliseVendor(data?.data || data);
    },
    enabled: !!vendorCode,
    staleTime: 1000 * 60 * 5,
  });
}

// ─── useVendorCases — cases assigned to a vendor ─────────────────────────────
//
// Response shape from GET /api/admin/vendors/:vendorCode/cases:
//   { success, nextCursor, data: [...] }
//
export function useVendorCases(vendorCode, filters = {}) {
  return useQuery({
    queryKey: VENDOR_KEYS.cases(vendorCode),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.status) params.append("status", filters.status);

      const { data } = await api.get(
        `/api/admin/vendors/${vendorCode}/cases?${params}`
      );

      const raw = data?.data || [];
      return {
        cases: raw.map((c) => ({
          id:               c.id,
          caseNo:           c.accidentCaseNo    || null,
          vehicleNo:        c.vehicleNumber     || null,
          currentStatus:    c.currentStatus     || null,
          assignedRole:     c.assignedRole      || null,   // "CRANE" | "RECOVERY_VAN"
          assignmentStatus: c.assignmentStatus  || null,
          driverName:       c.driverName        || null,
          driverNumber:     c.driverNumber      || null,
          createdAt:        c.createdAt         || null,
        })),
        nextCursor: data?.nextCursor || null,
      };
    },
    enabled: !!vendorCode,
    staleTime: 1000 * 60 * 2,
  });
}

export function useVendorByCode(vendorCode) {
  return useQuery({
    queryKey: ["vendor", "byCode", vendorCode],
    enabled: !!vendorCode,
    queryFn: async () => {
      const { data } = await api.get(`/admin/vendors/${vendorCode}`);
      return data?.data;
    },
  });
}

// ─── useNearbyVendors — vendors within a radius ───────────────────────────────
//
// Response shape from GET /api/admin/vendors/nearby:
//   { success, total, data: [...] }
//
export function useNearbyVendors({ latitude, longitude, radiusKm = 50, vendorType } = {}) {
  return useQuery({
    queryKey: VENDOR_KEYS.nearby({ latitude, longitude, radiusKm, vendorType }),
    queryFn: async () => {
      const params = new URLSearchParams({
        latitude,
        longitude,
        radiusKm,
        ...(vendorType ? { vendorType } : {}),
      });

      const { data } = await api.get(`/api/admin/vendors/nearby?${params}`);
      const raw = data?.data || [];

      return raw.map((v) => ({
        ...normaliseVendor(v),
        distanceKm: v.distanceKm != null ? Number(v.distanceKm) : null,
      }));
    },
    enabled: !!latitude && !!longitude,
    staleTime: 1000 * 60 * 2,
  });
}

export function useVendorDrivers(vendorCode, enabled = true) {
  return useQuery({
    queryKey: ["vendorDrivers", vendorCode],
    queryFn: async () => {
      const { data } = await api.get(`/api/admin/vendors/${vendorCode}/drivers`);
      return data; // { vendor, total, available, data: [...] }
    },
    enabled: !!vendorCode && enabled,
    staleTime: 30_000,
  });
}

export function useAdminAssignQSCase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ vendorCode, requestCode }) => {
      const { data } = await api.post(
        `/api/admin/vendors/${vendorCode}/assign-qs-case`,
        { requestCode }
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: VENDOR_KEYS.all() });
      qc.invalidateQueries({ queryKey: ["quickServices"] });
      qc.invalidateQueries({ queryKey: ["quickServiceRequests"] });
    },
  });
}

// ─── useCreateVendor ──────────────────────────────────────────────────────────
//
// POST /api/admin/vendors
// Payload: FormData (multipart) — matches addVendor controller expectations.
//
export function useCreateVendor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (formPayload) => {
      // formPayload can be a plain object or a FormData instance.
      // If it includes file fields (gstDoc, adharDoc …) the caller should
      // pass a FormData; otherwise we can send JSON.
      const isFormData = formPayload instanceof FormData;
      const { data } = await api.post("/api/admin/vendors", formPayload, {
        headers: isFormData ? { "Content-Type": "multipart/form-data" } : {},
      });
      return data;
    },
    onSuccess: () => {
      // Bust all vendor list caches so the new row appears
      qc.invalidateQueries({ queryKey: VENDOR_KEYS.all() });
    },
  });
}

// ─── useUpdateVendor ──────────────────────────────────────────────────────────
//
// PUT /api/admin/vendors/:vendorCode
// Payload: FormData or plain object — only send fields that changed.
//
export function useUpdateVendor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ vendorCode, ...rest }) => {
      if (!vendorCode) throw new Error("vendorCode is required");
      const isFormData = rest instanceof FormData;
      const { data } = await api.put(
        `/api/admin/vendors/${vendorCode}`,
        isFormData ? rest : rest,
        { headers: isFormData ? { "Content-Type": "multipart/form-data" } : {} }
      );
      return data;
    },
    onSuccess: (_, { vendorCode }) => {
      qc.invalidateQueries({ queryKey: VENDOR_KEYS.all() });
      qc.invalidateQueries({ queryKey: VENDOR_KEYS.detail(vendorCode) });
    },
  });
}

// ─── useDeleteVendor ──────────────────────────────────────────────────────────
//
// DELETE /api/admin/vendors/:vendorCode  (soft delete)
//
export function useDeleteVendor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vendorCode) => {
      if (!vendorCode) throw new Error("vendorCode is required");
      const { data } = await api.delete(`/api/admin/vendors/${vendorCode}`);
      return data;
    },
    onSuccess: (_, vendorCode) => {
      qc.invalidateQueries({ queryKey: VENDOR_KEYS.all() });
      qc.removeQueries({ queryKey: VENDOR_KEYS.detail(vendorCode) });
    },
  });
}

// ─── useToggleVendorStatus ────────────────────────────────────────────────────
//
// PATCH /api/admin/vendors/:vendorCode/status
// Body: { isActive: "1" | "0" }
//
export function useToggleVendorStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ vendorCode, isActive }) => {
      if (!vendorCode) throw new Error("vendorCode is required");
      const { data } = await api.patch(
        `/api/admin/vendors/${vendorCode}/status`,
        { isActive }
      );
      return data;
    },
    onSuccess: (_, { vendorCode }) => {
      qc.invalidateQueries({ queryKey: VENDOR_KEYS.all() });
      qc.invalidateQueries({ queryKey: VENDOR_KEYS.detail(vendorCode) });
    },
  });
}

// ─── useUpdateVendorLocation ──────────────────────────────────────────────────
//
// PATCH /api/admin/vendors/:vendorCode/location
// Body: { latitude, longitude }
//
export function useUpdateVendorLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ vendorCode, latitude, longitude }) => {
      if (!vendorCode) throw new Error("vendorCode is required");
      const { data } = await api.patch(
        `/api/admin/vendors/${vendorCode}/location`,
        { latitude, longitude }
      );
      return data;
    },
    onSuccess: (_, { vendorCode }) => {
      qc.invalidateQueries({ queryKey: VENDOR_KEYS.detail(vendorCode) });
      qc.invalidateQueries({ queryKey: VENDOR_KEYS.all() });
    },
  });
}

// ─── useAssignVendor ──────────────────────────────────────────────────────────
//
// POST /api/admin/cases/:caseId/assign-vendor
// Body: { vendorCode } (controller reads vendorCode from params but the route
//       is /cases/:caseId/assign-vendor, so vendorCode goes in body)
//
// Note: the controller reads `req.params.vendorCode` but the route is mounted
// as POST /api/admin/cases/:caseId/assign-vendor — meaning the caseId is the
// URL param and vendorCode must come from req.body.  The controller already
// receives vendorCode via req.params because assignCaseToVendor is shared.
// Adjust the body key below if your route wiring differs.
//
export function useAssignVendor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ caseId, vendorCode, vendorName }) => {
      if (!caseId)     throw new Error("caseId is required");
      if (!vendorCode) throw new Error("vendorCode is required");
      const { data } = await api.post(
        `/api/admin/cases/${caseId}/assign-vendor`,
        { vendorCode, vendorName }
      );
      return data;
    },
    onSuccess: (_, { vendorCode }) => {
      // Refresh this vendor's case list so it shows the newly assigned case
      qc.invalidateQueries({ queryKey: VENDOR_KEYS.cases(vendorCode) });
      // Also bust the global cases list in case CasesPage is open
      qc.invalidateQueries({ queryKey: ["cases"] });
    },
  });
}

// ─── Private helpers ──────────────────────────────────────────────────────────
function safeJson(val) {
  if (!val) return null;
  if (typeof val === "object") return val;
  try { return JSON.parse(val); } catch { return null; }
}